import type { LocaleSignals } from '@fraudeai/schema';
import { defineModule } from '../harness.js';
import { attempt } from '../util/safe.js';
import { hashValue } from '../util/hash.js';

/**
 * Смещение зоны IANA относительно UTC на заданный момент, в минутах на запад
 * (та же система знаков, что у Date.prototype.getTimezoneOffset).
 *
 * Считается через Intl, а не через Date, специально: если подменён только один
 * из двух источников, значения разойдутся — а это и есть искомый сигнал.
 */
function zoneOffsetMinutes(timeZone: string, date: Date): number | null {
  return attempt(() => {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    const parts = dtf.formatToParts(date);
    const num = (type: string): number => {
      const part = parts.find((p) => p.type === type);
      return part ? Number(part.value) : Number.NaN;
    };
    const asUtc = Date.UTC(
      num('year'),
      num('month') - 1,
      num('day'),
      num('hour') % 24,
      num('minute'),
      num('second'),
    );
    if (!Number.isFinite(asUtc)) return null;
    // Секундная точность: миллисекунды в formatToParts не попадают.
    const wall = Math.floor(date.getTime() / 1000) * 1000;
    return -Math.round((asUtc - wall) / 60000);
  });
}

export const localeModule = defineModule({
  id: 'locale',
  phase: 0,
  timeoutMs: 2000,
  run(): LocaleSignals {
    const resolved = attempt(() => new Intl.DateTimeFormat().resolvedOptions());
    const timezone = resolved?.timeZone ?? null;
    const locale = resolved?.locale ?? null;

    const year = new Date().getFullYear();
    const jan = new Date(year, 0, 1);
    const jul = new Date(year, 6, 1);
    const nowDate = new Date();

    const offsetNow = attempt(() => nowDate.getTimezoneOffset());
    const offsetJan = attempt(() => jan.getTimezoneOffset());
    const offsetJul = attempt(() => jul.getTimezoneOffset());

    const observesDst =
      offsetJan !== null && offsetJul !== null ? offsetJan !== offsetJul : null;

    // Сверяем два независимых источника одного и того же факта.
    let dstConsistent: boolean | null = null;
    if (timezone) {
      const viaIntlNow = zoneOffsetMinutes(timezone, nowDate);
      const viaIntlJan = zoneOffsetMinutes(timezone, jan);
      const viaIntlJul = zoneOffsetMinutes(timezone, jul);
      if (viaIntlNow !== null && viaIntlJan !== null && viaIntlJul !== null) {
        dstConsistent =
          viaIntlNow === offsetNow && viaIntlJan === offsetJan && viaIntlJul === offsetJul;
      }
    }

    const collationSample = attempt(() => {
      const words = ['ä', 'a', 'z', 'Z', 'å', 'ö', 'o', 'ß', 'é', 'e', '10', '9', 'Ω'];
      return [...words].sort(new Intl.Collator(undefined, { numeric: true }).compare);
    });

    const numberingSystem = attempt(() => new Intl.NumberFormat().resolvedOptions().numberingSystem);

    const intlPayload = {
      timezone,
      locale,
      calendar: resolved?.calendar ?? null,
      numberingSystem,
      hourCycle: resolved?.hourCycle ?? null,
      collationSample,
      currency: attempt(() =>
        new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(1234.56),
      ),
      date: attempt(() =>
        new Intl.DateTimeFormat(undefined, { dateStyle: 'full', timeStyle: 'long' }).format(
          new Date(0),
        ),
      ),
      // Список поддерживаемых зон различается между сборками ICU.
      zoneCount: attempt(() => (Intl as { supportedValuesOf?: (k: string) => string[] })
        .supportedValuesOf?.('timeZone').length ?? null),
    };

    return {
      // Показания часов клиента в момент замера. Сервер сравнит их со
      // своими и увидит расхождение — то, чего из самих настроек часового
      // пояса не видно: пояс и часы задаются независимо друг от друга.
      clockNow: attempt(() => Date.now()),
      timezone,
      timezoneOffsetNow: offsetNow,
      timezoneOffsetJan: offsetJan,
      timezoneOffsetJul: offsetJul,
      observesDst,
      dstConsistent,
      locale,
      calendar: resolved?.calendar ?? null,
      numberingSystem: numberingSystem ?? null,
      hourCycle: resolved?.hourCycle ?? null,
      firstDayOfWeek: attempt(() => {
        const L = (Intl as { Locale?: new (tag: string) => { getWeekInfo?: () => { firstDay: number } } })
          .Locale;
        if (!L || !locale) return null;
        return new L(locale).getWeekInfo?.().firstDay ?? null;
      }),
      currencySample: intlPayload.currency,
      dateSample: intlPayload.date,
      relativeTimeSample: attempt(() =>
        new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' }).format(-1, 'day'),
      ),
      collationSample,
      intlHash: hashValue(intlPayload),
    };
  },
});
